import i18n, { t } from 'i18next'
import type { EnhancedProductFamilyId, EnhancedProductGroupId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { MarketEntry } from '#app/store/slices/configuration/marketSlice'
import type { ConfigurationMode } from '#features/Configuration/ApiConfiguration'
import type { FavoriteEntryId } from '#features/FavoriteSets/FavoriteSet'
import { buildNationalCodesFilter } from '#features/NationalCodes/buildNationalCodesFilter'
import { filterNationalCodesTree } from '#features/NationalCodes/filterNationalCodesTree'
import { convertPrintOptionsFromManCode } from '#features/PrintOptions/convertPrintOptions'
import { translateBestEffort } from '#i18n/LocalizedString'
import Location from '#icons/location-48.svg'
import type { ConfigurationOptionalEquipment, TruckConfiguration } from '#store/slices/configurationSlice'
import type { ConfigurationEnrichmentData } from '#store/slices/enrichConfiguration'
import type {
  AppProductFamily,
  AppProductGroup,
  AppProductVariant,
  FamilyProductPath,
  GroupProductPath,
  VariantProductPath,
} from '#types/AppTypes.ts'
import type { PartPeriod } from '#utils/DateString'

const filterNationalCodes = true

export function enrichWithNationalCodes(
  enrichment: ConfigurationEnrichmentData,
  marketData: MarketEntry,
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  favoriteSet: Set<FavoriteEntryId>,
  mode: ConfigurationMode | undefined,
  language: string | undefined,
  partPeriod: PartPeriod | undefined,
  optionalEquipments: ConfigurationOptionalEquipment[],
  order: number,
) {
  if (!marketData) {
    console.warn('Market data is missing for national-code group')
    return
  }

  const filter = filterNationalCodes ? buildNationalCodesFilter(partPeriod) : undefined
  const groups = filterNationalCodesTree(marketData?.details?.groups, filter)

  const locales = language ? [language, i18n.language] : [i18n.language]

  const nationalCodesRootGroup: AppProductGroup = {
    source: 'national-code',
    id: 'national-codes' as EnhancedProductGroupId,
    title: t('configuration.national-codes.root-group.label', 'National Codes'),
    path: 'national-codes' as GroupProductPath,
    subGroups: [],
    families: [],
    isFavorite: false,
    parentGroup: undefined,
    iconUrl: Location,
    order,

    enrichment,
  }

  let hasNationalCodes = false

  for (const group of groups) {
    if (!group.families) {
      continue
    }

    if (!group.groupId) {
      console.error('Group ID is missing for national-code group', group)
      continue
    }

    order += 1

    const appGroup: AppProductGroup = {
      source: 'national-code',
      id: group.groupId as EnhancedProductGroupId,
      title: translateBestEffort(group.label, ...locales) ?? `Unnamed Group (${group.groupId})`,
      subGroups: [],
      families: [],
      path: `${nationalCodesRootGroup.path}/${group.groupId}` as GroupProductPath,
      isFavorite: favoriteSet.has(group.groupId as EnhancedProductGroupId),
      parentGroup: nationalCodesRootGroup,
      order,
      enrichment,
    }

    for (const family of group.families) {
      if (!family.familyId) {
        console.error('Family ID is missing for national-code family', group, family)
        continue
      }
      if (!family.codes) {
        continue
      }
      const familyId = family.familyId as EnhancedProductFamilyId
      const configuredFamily = configuredFamilies[familyId]
      const isImportant = marketData.attributes.find((x) => x.code === familyId)?.attributes.isImportant === true
      const isLocked = configuredFamily?.isLocked ?? false
      const isPartOfOptionalEquipment = optionalEquipments.some((x) => x.variants.some((x) => x.family.id === familyId))

      order += 1
      const appFamily: AppProductFamily = {
        source: 'national-code',
        id: familyId as EnhancedProductFamilyId,
        isImportant,
        isFavorite: favoriteSet.has(familyId),
        isLocked,
        isUserConfigured: false,
        parentGroup: appGroup,
        path: `${appGroup.path}/${family.familyId}` as FamilyProductPath,
        title: translateBestEffort(family.label, ...locales) ?? `Unnamed Family (${family.familyId})`,
        totalVariantCount: 0,
        selectionType: 'Default',
        hasNonSelectedFavorites: false,
        isPartOfOptionalEquipment,
        variants: [],

        detailData:
          family.description && Object.keys(family.description).length > 0
            ? {
                description: translateBestEffort(family.description, ...locales),
                resources: [],
              }
            : undefined,

        enrichment,
        order,
      }

      let hasFoundSelectedVariant = false
      for (let i = 0; i < family.codes.length; i++) {
        const code = family.codes[i]
        const isLastCode = i === family.codes.length - 1

        const initiallySelected = code.executionId === 'Z' || code.attributes?.withoutCode || (!hasFoundSelectedVariant && isLastCode)
        const isSelected = configuredFamily?.selectedVariant ? configuredFamily.selectedVariant.id === code.executionId : initiallySelected
        const isUserConfigured = configuredFamily?.selectedVariant?.id === code.executionId
        const isLocked = configuredFamily?.isLocked ?? false
        const isPartOfOptionalEquipment = optionalEquipments.some((x) =>
          x.variants.some((v) => v.family.id === family.familyId && v.targetVariant.id === code.executionId),
        )
        const isImportant = marketData.attributes.find((x) => x.code === code.executionId)?.attributes.isImportant === true

        const printOptions =
          configuredFamily?.printOptions[code.executionId as EnhancedProductVariantId] ??
          convertPrintOptionsFromManCode(code.attributes?.printingOptions)

        order += 1
        const appVariant: AppProductVariant = {
          source: 'national-code',
          id: code.executionId as EnhancedProductVariantId,

          title: translateBestEffort(code.label, ...locales) ?? `Unnamed Variant (${code.codeId})`,
          parentFamily: appFamily,
          path: `${appFamily.path}/${code.executionId}` as VariantProductPath,

          initiallySelected,
          isImportant,
          isSelected,
          isUserConfigured,
          isLocked,

          isFavorite: favoriteSet.has(`${familyId}${code.executionId as EnhancedProductVariantId}`),

          priceData:
            code.attributes && typeof code.attributes.grossListPrice === 'number' && !code.attributes.withoutCode
              ? {
                  price: code.attributes.grossListPrice,
                  deltaPrice: 0,
                }
              : undefined,

          insightsData: code.attributes
            ? {
                topOption: code.attributes.topOption,
                commissionRelevant: code.attributes.commissionRelevant,
                orderRelevant: code.attributes.orderRelevant,
                relevantForMaintenanceAndRepairBenefits: code.attributes.relevantForMaintenanceAndRepairBenefits,
                relevantForResidualValue: code.attributes.relevantForResidualValue,
              }
            : undefined,

          printOptions,
          isPartOfOptionalEquipment,

          detailData:
            code.description && Object.keys(code.description).length > 0
              ? {
                  description: translateBestEffort(code.description, ...locales),
                  resources: [],
                }
              : undefined,

          enrichment,
          compatibility: 'unknown',
          compatibilityStatus: 'compatible',
          order,
        }

        if (mode === 'read-only' && !isSelected) {
          continue
        }

        if (isSelected) {
          appFamily.selectedVariant = appVariant
          hasFoundSelectedVariant = true
        }
        if (!isSelected && appVariant.isFavorite) {
          appFamily.hasNonSelectedFavorites = true
        }
        if (initiallySelected) {
          appFamily.initialSelectedVariant = appVariant
        }
        if (isUserConfigured) {
          appFamily.isUserConfigured = true
        }

        appFamily.variants.push(appVariant)
        enrichment.enrichedVariants[`${appFamily.id}${appVariant.id}`] = appVariant
        enrichment.byPath[appVariant.path] = appVariant
      }

      if (appFamily.variants.length === 0) {
        continue
      }

      appGroup.families.push(appFamily)
      enrichment.enrichedFamilies[appFamily.id] = appFamily
      enrichment.byPath[appFamily.path] = appFamily
      appFamily.totalVariantCount = appFamily.variants.length
    }

    if (appGroup.families.length === 0) {
      continue
    }

    nationalCodesRootGroup.subGroups.push(appGroup)
    enrichment.enrichedGroups[appGroup.id] = appGroup
    enrichment.byPath[appGroup.path] = appGroup

    hasNationalCodes = true
  }

  if (hasNationalCodes) {
    enrichment.rootGroups.push(nationalCodesRootGroup)
    enrichment.enrichedGroups[nationalCodesRootGroup.id] = nationalCodesRootGroup
    enrichment.byPath[nationalCodesRootGroup.path] = nationalCodesRootGroup
  }
}
